import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { Input, Popover, VStack } from 'govwell-ui'
import { useMenuKeyboardShortcuts } from 'govwell-ui/components/Menu/use-menu-keyboard-shortcuts'

import { useActiveOrgId } from 'src/hooks/use-active-org-id'

import { useMyOrganizationMemberships } from '../../hooks/use-my-organization-memberships'

import OrgRow, { getOrgLabel } from './OrgRow'

const SEARCH_THRESHOLD = 5

type Props = {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  children: React.ReactNode
}

const OrgSwitcherPopover = (props: Props) => {
  const { isOpen, onOpen, onClose, children } = props
  const memberships = useMyOrganizationMemberships()
  const currentOrgId = useActiveOrgId()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [searchQuery, setSearchQuery] = useState('')

  const showSearch = memberships.length > SEARCH_THRESHOLD

  const filteredMemberships = useMemo(() => {
    if (!searchQuery) {
      return memberships
    }
    const query = searchQuery.toLowerCase()
    return memberships.filter((m) => getOrgLabel(m).toLowerCase().includes(query))
  }, [memberships, searchQuery])

  const currentOrgIndex = useMemo(
    () =>
      Math.max(
        0,
        filteredMemberships.findIndex((m) => m.organizationId === currentOrgId)
      ),
    [filteredMemberships, currentOrgId]
  )

  const [activeIndex, setActiveIndex] = useState(currentOrgIndex)

  const prevIsOpenRef = useRef(false)
  // Reset active index and search query when the popover first opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setActiveIndex(currentOrgIndex)
      setSearchQuery('')
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, currentOrgIndex])

  // Reset active index when filtered results change (e.g. during search)
  useEffect(() => {
    setActiveIndex(0)
  }, [filteredMemberships.length])

  // Focus search input when popover opens
  const handleOpenAutoFocus = useCallback(
    (e: Event) => {
      if (showSearch) {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    },
    [showSearch]
  )

  const handleSelect = useCallback(() => {
    // No-op for now — org switching logic will be wired up later
  }, [])

  const { menuItemRefs } = useMenuKeyboardShortcuts({
    activeIndex,
    count: filteredMemberships.length,
    isEnabled: isOpen && filteredMemberships.length > 0,
    onEscape: onClose,
    onSelect: handleSelect,
    setActiveIndex,
  })

  const orgRows = useMemo(
    () =>
      filteredMemberships.map((membership, index) => (
        <OrgRow
          key={membership.id}
          membership={membership}
          isCurrentOrg={membership.organizationId === currentOrgId}
          isHighlighted={index === activeIndex}
          onMouseMove={() => setActiveIndex(index)}
          rowRef={(el) => menuItemRefs.current.set(index, el)}
          onClick={handleSelect}
        />
      )),
    [filteredMemberships, currentOrgId, activeIndex, menuItemRefs, handleSelect]
  )

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Content
        side="top"
        align="start"
        sideOffset={8}
        className="w-80 p-1"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        {showSearch && (
          <Input
            ref={searchInputRef}
            ariaLabel="Search organizations"
            className="mx-1 mt-1 mb-1.5"
            placeholder="Search organizations..."
            prefixIcon={faSearch}
            value={searchQuery}
            onValueChange={setSearchQuery}
            isClearable
          />
        )}
        <VStack gap={0} align="stretch" maxHeight="288px" className="overflow-y-auto">
          {orgRows}
          {filteredMemberships.length === 0 && (
            <p className="px-3 py-2 text-center text-sm text-neutral-500">No results</p>
          )}
        </VStack>
      </Popover.Content>
    </Popover>
  )
}

export default React.memo(OrgSwitcherPopover)
